export type DpaType = {
  id: string;
  label: string;
  short: string;
  identify: string;
};

export const DPA_TYPES: DpaType[] = [
  {
    id: "grant",
    label: "True grant",
    short: "No repayment, no lien in most cases",
    identify:
      "Your closing package shows the funds as a gift or grant with no note and no second deed of trust. Some grants still carry a short occupancy or affordability period recorded against the property, so read the award letter as well as the settlement statement. Terms vary by program.",
  },
  {
    id: "forgivable",
    label: "Forgivable second loan",
    short: "Forgives over time; unforgiven balance is due early",
    identify:
      "There is a promissory note and a recorded second lien, usually at 0% interest, that forgives across a retention period. Forgiveness may be prorated year by year or all-or-nothing at the end of the period. Terms vary by program.",
  },
  {
    id: "deferred",
    label: "Deferred-payment loan",
    short: "No monthly payment, full balance due on sale or refinance",
    identify:
      "A note and second lien with payments deferred — often to the earlier of sale, refinance, transfer of title, or the end of the first mortgage term. Nothing is forgiven; the balance simply waits. Terms vary by program.",
  },
  {
    id: "amortizing",
    label: "Amortizing second mortgage",
    short: "You already pay it monthly; payoff is the remaining principal",
    identify:
      "You make a monthly payment on the assistance itself, often at a low fixed rate. On sale or refinance you pay the remaining principal plus any accrued interest and payoff fees. Terms vary by program.",
  },
];

export type ScenarioBlock = {
  id: string;
  type: string;
  payoffTiming: string;
  balanceLogic: string;
  closingImpact: string;
};

export const SELL_SCENARIOS: ScenarioBlock[] = [
  {
    id: "sell-grant",
    type: "True grant",
    payoffTiming:
      "Usually nothing is due at sale if no lien or occupancy period was recorded. If an affordability period was recorded and you sell inside it, the payoff is triggered at closing.",
    balanceLogic:
      "Either $0, or the amount named in the recorded restriction — some programs also recapture a share of appreciation instead of the original dollars.",
    closingImpact:
      "Title runs a lien search regardless. If nothing is recorded, the grant never appears on the settlement statement. Terms vary by program.",
  },
  {
    id: "sell-forgivable",
    type: "Forgivable second loan",
    payoffTiming:
      "Due at closing if you sell before the forgiveness period ends. After the period ends and the lien is released, nothing is due.",
    balanceLogic:
      "Prorated programs charge only the unforgiven share (for example, selling in year 3 of a 5-year straight-line schedule leaves roughly 40% owed). All-or-nothing programs charge the full original amount at any point inside the period.",
    closingImpact:
      "The payoff comes out of your seller proceeds as a second-lien payoff line. If proceeds are thin, you may need to bring cash. Terms vary by program.",
  },
  {
    id: "sell-deferred",
    type: "Deferred-payment loan",
    payoffTiming: "Due in full at closing — sale is the classic maturity trigger for these loans.",
    balanceLogic:
      "The full original principal, plus any accrued interest if the note is not 0%, plus a payoff or reconveyance fee if the program charges one.",
    closingImpact:
      "Shows as a full second-lien payoff on the seller side of the settlement statement. Terms vary by program.",
  },
  {
    id: "sell-amortizing",
    type: "Amortizing second mortgage",
    payoffTiming: "Due at closing, same as any other mortgage lien.",
    balanceLogic:
      "Remaining principal plus per-diem interest through the payoff date. Because you have been paying it down, this is normally the smallest of the four balances.",
    closingImpact:
      "Standard payoff line. Order the payoff statement early — it usually expires within 10 to 30 days. Terms vary by program.",
  },
];

export const REFI_SCENARIOS: ScenarioBlock[] = [
  {
    id: "refi-grant",
    type: "True grant",
    payoffTiming:
      "Typically nothing to pay off, but if an occupancy or affordability covenant was recorded, your new lender will require it to be addressed before closing.",
    balanceLogic:
      "Usually $0. Where a covenant exists, the program decides whether to re-subordinate it or call it.",
    closingImpact:
      "Title will surface any recorded restriction during the refinance search. Terms vary by program.",
  },
  {
    id: "refi-forgivable",
    type: "Forgivable second loan",
    payoffTiming:
      "A refinance pays off and replaces your first mortgage, which moves the second lien into first position unless the program agrees to subordinate. Programs generally either subordinate or require payoff.",
    balanceLogic:
      "If subordinated, the forgiveness clock usually continues and nothing is paid. If payoff is required, the unforgiven balance is due at closing — refinancing does not always restart or reset forgiveness. Confirm with your program.",
    closingImpact:
      "Payoff has to come from refinance proceeds or your own cash, which can wipe out the savings you were refinancing to capture. Terms vary by program.",
  },
  {
    id: "refi-deferred",
    type: "Deferred-payment loan",
    payoffTiming:
      "Subordination is the make-or-break question. Many agencies subordinate for a rate-and-term refinance and decline for cash-out.",
    balanceLogic:
      "If subordination is denied, the entire deferred balance plus any accrued interest comes due at the refinance closing.",
    closingImpact:
      "Either a re-recorded subordinate lien (small fee) or a full payoff line item. Terms vary by program.",
  },
  {
    id: "refi-amortizing",
    type: "Amortizing second mortgage",
    payoffTiming:
      "Same subordination question. Some borrowers choose to roll the second into the new first loan instead.",
    balanceLogic:
      "Remaining principal plus per-diem interest. Rolling it in raises your new loan amount and can change your loan-to-value tier and mortgage insurance.",
    closingImpact:
      "Watch the LTV effect before you assume consolidating is cheaper. Terms vary by program.",
  },
];

export const COMPARISON_ROWS: {
  question: string;
  sell: string;
  refi: string;
}[] = [
  {
    question: "Does the lien have to be cleared?",
    sell: "Yes — title cannot transfer with an unresolved lien.",
    refi: "Not always — subordination can keep it in place behind the new loan.",
  },
  {
    question: "Who decides the outcome?",
    sell: "The program's payoff statement plus your closing agent.",
    refi: "The program's subordination department and your new lender, together.",
  },
  {
    question: "Where does the money come from?",
    sell: "Your sale proceeds at closing.",
    refi: "Refinance proceeds or out of pocket — there are no sale proceeds.",
  },
  {
    question: "Does forgiveness keep running?",
    sell: "No — the retention period ends with your ownership.",
    refi: "Usually yes if subordinated; usually no if paid off. Confirm in writing.",
  },
  {
    question: "Typical lead time to request paperwork",
    sell: "Order the payoff statement as soon as you are under contract.",
    refi: "Request subordination guidelines before you apply, not after you lock.",
  },
  {
    question: "Most common surprise",
    sell: "Unforgiven balance larger than expected because forgiveness is all-or-nothing.",
    refi: "Subordination denied on a cash-out, forcing an unplanned payoff.",
  },
];

export const FORGIVENESS_TIMELINE = [
  { year: "Year 1", remaining: 100 },
  { year: "Year 2", remaining: 80 },
  { year: "Year 3", remaining: 60 },
  { year: "Year 4", remaining: 40 },
  { year: "Year 5", remaining: 20 },
  { year: "After", remaining: 0 },
];

export const GATHER_CHECKLIST = [
  "Your second-lien promissory note and recorded deed of trust or mortgage",
  "The program award letter or commitment letter from closing",
  "The final Closing Disclosure or HUD-1 from your purchase",
  "The exact program name, loan number, and servicing contact",
  "Your purchase date and the stated retention or affordability period",
  "Whether your loan was linked to a mortgage revenue bond or an MCC",
  "Your current first-mortgage payoff estimate and estimated home value",
  "For a refinance: the new loan amount, term, rate, and whether it is cash-out",
];

export const DPA_FAQS = [
  {
    id: "cash-at-closing",
    topic: "cash_at_closing",
    q: "Will I have to bring cash to closing to pay back down payment assistance?",
    a: "Only if your sale proceeds or refinance proceeds do not cover the payoff. On a sale, the second-lien payoff comes out of your proceeds first; you bring cash only when the home sells for less than the combined payoffs and costs. On a refinance there are no proceeds unless you take cash out, so a required payoff usually has to come from the new loan amount or from savings. Ask for a written payoff quote early so you can model it before you commit. Terms vary by program.",
  },
  {
    id: "refi-denial",
    topic: "refinance_denial",
    q: "What happens if my subordination request is denied?",
    a: "A denial does not force you to refinance. It means that if you go forward, the assistance lien must be paid off at closing so the new first mortgage records in first position. Your practical options are to pay it off, roll it into the new loan if the numbers still work, or keep your current mortgage. Denials are most common on cash-out refinances, on requests that fail a net-tangible-benefit test, or when the new loan amount pushes combined loan-to-value past the program limit. Terms vary by program.",
  },
  {
    id: "forgiven-returns",
    topic: "forgiveness_reversal",
    q: "Can assistance that was already forgiven come back after I sell?",
    a: "Forgiven and released amounts generally do not come back — once the lien is released, that portion is gone. Two things still catch people: the portion that had not yet been forgiven when you sold, and separate recapture tax on bond-financed loans, which is a federal tax question rather than a repayment to the housing agency. Confirm the lien release was recorded, and keep the release document. Terms vary by program.",
  },
  {
    id: "recapture-tax",
    topic: "recapture_tax",
    q: "Is recapture tax the same thing as repaying my assistance?",
    a: "No. Repayment goes to the housing agency or lender that holds your second lien. Recapture tax is a federal tax that can apply to loans financed with mortgage revenue bonds if you sell at a gain within roughly the first nine years and your income has risen above program limits at the time of sale. Many borrowers who qualify on paper end up owing nothing because of how the tests interact. Confirm whether your loan was bond-financed and talk to a tax professional before you assume either way.",
  },
  {
    id: "cash-out-limits",
    topic: "cash_out_limits",
    q: "Can I take cash out and keep my down payment assistance in place?",
    a: "Often no. Many programs will subordinate for a rate-and-term refinance but not for cash-out, because pulling equity out reduces the cushion protecting their subordinate position. Some allow limited cash out for documented purposes such as home repairs. Ask for the written subordination policy — including any combined loan-to-value cap and processing fee — before you apply. Terms vary by program.",
  },
];
